/* eslint-disable */
import { createEntityAdapter } from '@reduxjs/toolkit';
import { differenceInDays } from "date-fns";

import { Policy } from '../models/policy';
import { PolicyInstallment } from '../models/policyInstallment';
import { SubmitAmendmentRequest } from '../models/request/submitAmendmentRequest';
import { PolicyStatusDefinition } from '../models/policyStatusDefinition';
import { UpdatePolicyStatusRequest } from '../models/request/UpdatePolicyStatusRequest';
import api from '../redux/api';
import { AmendPolicy } from '../models/amendPolicy';
import { SubmitInsuranceProductRequest } from '../models/request/submitInsuranceProductRequest';
import { DownloadInvoiceDocumentRequest } from '../models/request/DownloadInvoiceDocumentRequest';
import { Broker } from '../models/broker';
import { UpdatePolicyBackOfficeDetailsRequest } from '../models/request/UpdatePolicyBackOfficeDetailsRequest';
import { PolicyBackOfficeDetailsResponse } from '../models/response/PolicyBackOfficeDetailsResponse';
import { ProductPreview } from '../models/response/ProductPreview';
import { environment } from '../environments/environment';
import { DocumentInfo } from '../models/documentInfo';
import { DocumentRequest, DownloadDocumentRequest } from '../models/request/documentRequest';

const policiesAdapter = createEntityAdapter({
  selectId: (e: Policy) => e.uniqueIdentifier,
  sortComparer: (f, s) => f.id < s.id ? 1 : (f.id === s.id ? 0 : -1)
});

export const policiesApiSlice = api.injectEndpoints({
  endpoints: (build) => ({
    getPolicies: build.query<Policy[], void>({
      query: () => "insProd/BaseCustomerData/policies/full-details",
      providesTags: ["Policy"],
      transformResponse: (response: any[]) => response?.map((dto: any) => new Policy(dto)),
    }),
    getProductStatusDefinition: build.query<any, any>({
      query: (productVersionCode: string) => `insProd/BaseCustomerData/products/version/${productVersionCode}/statuses`,
    }),
    submitAmendment: build.mutation<any, SubmitAmendmentRequest>({
      invalidatesTags: ["Policy"],
      query: (body) => ({
        url: "insProd/BaseCustomerData/policies/amendments/submit",
        method: "POST",
        body,
      })
    }),
    getPolicyInstallments: build.query<PolicyInstallment[], void>({
      query: () => "insProd/BaseCustomerData/installments",
    }),
    getAvailableStatusTransitions: build.query<PolicyStatusDefinition[], string>({
      query: (policyUId) => `insProd/BaseCustomerData/policies/${policyUId}/status/available-transitions`,
    }),
    updatePolicyStatus: build.mutation<any, UpdatePolicyStatusRequest>({
      invalidatesTags: ["Policy"],
      query: (body) => ({
        url: "insProd/BaseCustomerData/policies/status/update",
        method: "POST",
        body,
      }),
    }),
    canAmendPolicy: build.query<AmendPolicy[], void>({
      query: () => "insProd/BaseCustomerData/policies/amendable",
      transformResponse: (response: any[]) => response?.map((x: any) => new AmendPolicy(x)).filter(x =>([1, 24].includes(x.subStatusId) && differenceInDays(x.validFromDate, new Date()) <= environment.settings.numberOfInceptionDays)),
    }),
    calculatePrice: build.mutation<any, SubmitInsuranceProductRequest>({
      query: (body) => ({
        url: "insProd/BaseCustomerData/policies/calculate-price",
        method: "POST",
        body
      })
    }),
    getPolicyDocuments: build.query<DocumentInfo[], string>({
      query: (policyUId) => `insProd/BaseCustomerData/policies/${policyUId}/documents`,
    }),
    getPoliciesDocuments: build.mutation<DocumentInfo[], DocumentRequest>({
      query: (body) => ({
        url: `insProd/BaseCustomerData/policies/documents`,
        method: "POST",
        body
      }),
    }),
    downloadPolicyDocument: build.query<any, DownloadDocumentRequest>({
      query: ({ entityIdentifier, documentId }) => ({
        url: `insProd/BaseCustomerData/policies/${entityIdentifier}/documents/${documentId}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    downloadInvoiceDocument: build.query<any, DownloadInvoiceDocumentRequest>({
      query: ({ policyUId, invoiceUId }) => ({
        url: `insProd/BaseCustomerData/policies/${policyUId}/invoices/${invoiceUId}/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getBroker: build.query<Broker, string>({
      query: (policyUId: string) => `insProd/BaseCustomerData/policies/${policyUId}/broker-details`,
      transformResponse: (response: any) => new Broker(response),
    }),
    getBackOfficeDetails: build.query<PolicyBackOfficeDetailsResponse, string>({
      query: (policyUId: string) => `insProd/BaseCustomerData/policies/${policyUId}/backoffice-details`,
      providesTags: ["Policy"]
    }),
    updateBackOfficeDetails: build.mutation<any, UpdatePolicyBackOfficeDetailsRequest>({
      invalidatesTags: ["Policy"],
      query: (body) => ({
        url: "insProd/BaseCustomerData/policies/backoffice-details/update",
        method: "POST",
        body
      }),
    }),
    getProducts: build.query<ProductPreview[], void>({
      query: () => "insProd/BaseCustomerData/products",
    }),
  }),
});

export const {
  useGetPoliciesQuery,
  useGetProductStatusDefinitionQuery,
  useSubmitAmendmentMutation,
  useGetPolicyInstallmentsQuery,
  useGetAvailableStatusTransitionsQuery,
  useUpdatePolicyStatusMutation,
  useCanAmendPolicyQuery,
  useCalculatePriceMutation,
  useGetPoliciesDocumentsMutation,
  useGetPolicyDocumentsQuery,
  useDownloadPolicyDocumentQuery,
  useDownloadInvoiceDocumentQuery,
  useGetBrokerQuery,
  useGetBackOfficeDetailsQuery,
  useUpdateBackOfficeDetailsMutation,
  useGetProductsQuery
} = policiesApiSlice;

export const {
  selectAll: selectAllPolicies,
  selectById: selectPolicyByIdentifier,
  selectIds: selectPolicyUniqeIdentifiers
} = policiesAdapter.getSelectors();
